// import { apiService } from './api';

export interface StreamEvent {
  type: 'status' | 'chunk' | 'complete' | 'error';
  message?: string;
  content?: string;
  done?: boolean;
  tokenCount?: number;
  sources?: DocumentSource[];
  confidence?: number;
  processingTime?: number;
  needsFiltering?: boolean;
  followUpQuestions?: string[];
}

export interface DocumentSource {
  id: number;
  documentId: number;
  content: string;
  similarity: number;
  page: number;
  section: string;
}

export interface StreamingQuery {
  question: string;
  documentId?: number;
  sessionId?: string;
  options?: {
    temperature?: number;
    maxTokens?: number;
    systemPrompt?: string;
  };
}

export class StreamingService {
  private currentEventSource: EventSource | null = null;

  /**
   * Start a streaming RAG query
   * @param query - The query parameters
   * @param onEvent - Callback for each streaming event
   * @returns Promise that resolves when streaming completes
   */
  async streamQuery(
    query: StreamingQuery,
    onEvent: (event: StreamEvent) => void
  ): Promise<void> {
    // Close any existing stream
    this.closeStream();

    return new Promise((resolve, reject) => {
      try {
        // Create EventSource for Server-Sent Events
        const eventSource = new EventSource(
          `${this.getStreamUrl()}?${this.buildQueryParams(query)}`,
          {
            withCredentials: false
          }
        );

        this.currentEventSource = eventSource;

        // Handle incoming events
        eventSource.onmessage = (event) => {
          try {
            if (event.data === '[DONE]') {
              this.closeStream();
              resolve();
              return;
            }

            const data: StreamEvent = JSON.parse(event.data);
            onEvent(data);

            // Check for completion
            if (data.type === 'complete' || data.type === 'error') {
              this.closeStream();
              if (data.type === 'error') {
                reject(new Error(data.message || 'Streaming failed'));
              } else {
                resolve();
              }
            }
          } catch (error) {
            console.error('Error parsing stream event:', error);
            onEvent({
              type: 'error',
              message: 'Failed to parse streaming response'
            });
          }
        };

        // Handle connection errors
        eventSource.onerror = (error) => {
          console.error('EventSource error:', error);
          this.closeStream();
          reject(new Error('Streaming connection failed'));
        };

        // Start the stream by making a POST request
        this.initiateStream(query);

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Close the current streaming connection
   */
  closeStream(): void {
    if (this.currentEventSource) {
      this.currentEventSource.close();
      this.currentEventSource = null;
    }
  }

  /**
   * Check if streaming is currently active
   */
  isStreaming(): boolean {
    return this.currentEventSource !== null;
  }

  /**
   * Alternative method using fetch with manual SSE parsing
   * This is needed because EventSource doesn't support POST requests
   */
  async streamQueryWithFetch(
    query: StreamingQuery,
    onEvent: (event: StreamEvent) => void
  ): Promise<void> {
    console.log('🔍 DEBUG: Sending query to backend:', JSON.stringify(query, null, 2));
    try {
      const response = await fetch('https://api-jurisai.polykloud.com/api/queries/ask-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Authorization': 'Bearer demo-token',
        },
        body: JSON.stringify(query),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body for streaming');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              if (data === '[DONE]') {
                return;
              }

              try {
                const event: StreamEvent = JSON.parse(data);
                onEvent(event);

                if (event.type === 'error') {
                  // Handle specific RAG errors more gracefully
                  if (event.message && event.message.includes("couldn't find any relevant information")) {
                    onEvent({
                      type: 'error',
                      message: 'No relevant information found in your documents. Try rephrasing your question or check if your documents have been properly processed.'
                    });
                  } else {
                    throw new Error(event.message || 'Streaming failed');
                  }
                  return;
                }
              } catch (parseError) {
                // Check if this is a non-SSE error response (plain JSON)
                try {
                  const errorResponse = JSON.parse(data);
                  if (errorResponse.type === 'error' || errorResponse.message) {
                    // Handle specific RAG errors more gracefully
                    if (errorResponse.message && errorResponse.message.includes("couldn't find any relevant information")) {
                      onEvent({
                        type: 'error',
                        message: 'No relevant information found in your documents. Try rephrasing your question or check if your documents have been properly processed.'
                      });
                    } else {
                      onEvent({
                        type: 'error',
                        message: errorResponse.message || 'Unknown error occurred'
                      });
                    }
                    return;
                  }
                } catch {
                  // Skip invalid lines that aren't JSON either
                  console.warn('Failed to parse SSE line:', data);
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      onEvent({
        type: 'error',
        message: error instanceof Error ? error.message : 'Streaming failed'
      });
      throw error;
    }
  }

  private getStreamUrl(): string {
    return 'https://api-jurisai.polykloud.com/api/queries/ask-stream';
  }

  private buildQueryParams(query: StreamingQuery): string {
    const params = new URLSearchParams();
    params.append('question', query.question);
    
    if (query.documentId) {
      params.append('documentId', query.documentId.toString());
    }
    
    if (query.options?.temperature) {
      params.append('temperature', query.options.temperature.toString());
    }
    
    return params.toString();
  }

  private async initiateStream(_query: StreamingQuery): Promise<void> {
    // This is handled by the fetch-based implementation
    // EventSource doesn't support POST, so we use streamQueryWithFetch instead
  }
}

export const streamingService = new StreamingService();
export default streamingService;
